/**
 * Workspace lock — is this org read-only because its trial ended (or its
 * subscription was cancelled)?
 *
 * The backend is the real gate: an expired org resolves to the locked
 * entitlement set (every limit 0), so create calls come back 402. This is the
 * UI half of that contract — without it every "Connect" / "New …" / "Add …"
 * button still looked live, and the only feedback was an error after the user
 * had filled in a whole form.
 *
 * One request, one source of truth: the entitlements snapshot is fetched once
 * per page load and shared, so the billing banner and every gated button agree
 * and we don't fire the same GET from a dozen components.
 */
import { useEffect, useState } from 'react';
import { SubscriptionAPI } from '../apis/Subscription';

let cached = null;      // resolved snapshot for this page load
let inflight = null;    // de-dupes concurrent first callers
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => {
    try { fn(cached); } catch (e) { /* one bad listener must not stop the rest */ }
  });
}

/**
 * Watch for entitlement changes. Returns an unsubscribe function.
 *
 * Needed because the snapshot is cached for the page load: after a successful
 * checkout the org is no longer locked, but nothing would have told the billing
 * banner or the gated buttons to re-read — so the "trial has ended" strip stayed
 * up, and every create button stayed grey, until a manual refresh.
 */
export function subscribeEntitlements(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Replace the shared snapshot with one the caller already has (e.g. Plan &
 * Usage re-reads entitlements after every billing action) and wake watchers.
 * Preferred over `invalidateEntitlements` when you hold fresh data: it costs no
 * extra request.
 */
export function publishEntitlements(snap) {
  cached = snap || null;
  inflight = null;
  notify();
}

/** Entitlements snapshot, fetched at most once per page load. */
export function fetchEntitlements() {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = SubscriptionAPI.getEntitlements()
      .then((res) => {
        cached = res?.data || res || null;
        inflight = null;
        return cached;
      })
      .catch(() => {
        // Billing awareness is a nicety — never let it break a page. Failing
        // open (unlocked) is deliberate: the backend still refuses the write,
        // so the worst case is the old behaviour, not a wrongly-blocked user.
        inflight = null;
        return null;
      });
  }
  return inflight;
}

/** Drop the cache and make watchers re-fetch. Use when the plan changed but you
 *  don't hold the new snapshot; prefer `publishEntitlements` when you do. */
export function invalidateEntitlements() {
  cached = null;
  inflight = null;
  notify();
}

export const LOCK_MESSAGE = {
  expired: 'Your trial has ended — choose a plan to create new resources.',
  canceled: 'Your subscription was cancelled — choose a plan to create new resources.',
};

/** Derive the lock from a snapshot. Kept separate so it is trivially testable. */
export function lockFromSnapshot(snap) {
  const status = snap && snap.status;
  // Personal workspaces are free forever and never lock.
  if (!snap || snap.plan === 'personal') return { locked: false, message: '' };
  if (status === 'expired' || status === 'canceled') {
    return { locked: true, status, message: LOCK_MESSAGE[status] };
  }
  return { locked: false, message: '' };
}

/**
 * `{ locked, message }` for the current org.
 *
 * Starts unlocked and flips once the snapshot arrives, so a slow request never
 * blocks the page — it just means the button is briefly live. Use `message` as
 * the tooltip on anything you disable.
 */
export function useWorkspaceLock() {
  const [snap, setSnap] = useState(cached);

  useEffect(() => {
    let alive = true;
    const read = () => {
      if (cached) { setSnap(cached); return; }
      // Cache was dropped (plan changed) — pull the new state.
      fetchEntitlements().then((s) => { if (alive) setSnap(s); });
    };
    read();
    // Re-read when the plan changes, so an org that just subscribed stops being
    // treated as locked without needing a page refresh.
    const off = subscribeEntitlements(() => { if (alive) read(); });
    return () => { alive = false; off(); };
  }, []);

  return lockFromSnapshot(snap);
}

export default useWorkspaceLock;
